import React from 'react';
import Modal, { ModalFooter } from '@amzn/meridian/modal';
import Row from '@amzn/meridian/row';
import Button from '@amzn/meridian/button';
import Alert from '@amzn/meridian/alert';
import Text from '@amzn/meridian/text';
import { useAppSelector, useAppDispatch } from '../../store';
import { selectCurrentError, selectHasErrors, dismissError, NotificationType } from '../../store/slices/errorSlice';

/**
 * Maps notification type to Meridian Alert type.
 * Alert supports: "error" | "warning" | "success" | "neutral" | "theme"
 */
const getAlertType = (type: NotificationType): "error" | "warning" | "success" | "neutral" | "theme" => {
  switch (type) {
    case 'error': return 'error';
    case 'warning': return 'warning';
    case 'success': return 'success';
    case 'info': return 'neutral';
    default: return 'neutral';
  }
};

/**
 * Returns the appropriate button label based on notification type.
 */
const getButtonLabel = (type: NotificationType): string => {
  switch (type) {
    case 'success': return 'OK';
    case 'error': return 'Dismiss';
    case 'warning': return 'Understood';
    case 'info': return 'OK';
    default: return 'OK';
  }
};

/**
 * NotificationModal — handles all notification types (error, success, warning, info)
 * using Meridian Modal with Alert component for semantic color/icon treatment.
 * Replaces browser alert() calls throughout the application.
 */
const ErrorModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const currentError = useAppSelector(selectCurrentError);
  const hasErrors = useAppSelector(selectHasErrors);

  const handleDismiss = () => {
    dispatch(dismissError());
  };

  if (!hasErrors || !currentError) {
    return null;
  }

  const notificationType = currentError.type || 'error';
  const alertType = getAlertType(notificationType);
  const buttonLabel = getButtonLabel(notificationType);

  return (
    <Modal
      title={currentError.title}
      open={hasErrors}
      onClose={handleDismiss}
      scrollContainer="viewport"
      aria-describedby="notification-modal-description"
    >
      <Alert
        type={alertType}
        size="large"
      >
        <Text id="notification-modal-description" tag="p">
          <span style={{ whiteSpace: 'pre-line' }}>{currentError.message}</span>
        </Text>
      </Alert>
      <ModalFooter>
        <Row alignmentHorizontal="end" widths="fit">
          <Button 
            type="primary" 
            size="small" 
            onClick={handleDismiss}
          >
            {buttonLabel}
          </Button>
        </Row>
      </ModalFooter>
    </Modal>
  );
};

export default ErrorModal;

import './ErrorModal.scss';